import {FC, useRef, useState} from 'react'
import {useIntl} from 'react-intl'
import {get} from 'lodash'
import {ContactDetailsType, IContactDto, IContactFilter} from '../Contact.model'
import {ContactPrimarySelectorListItem} from './ContactPrimarySelectorListItem'
import {FormikProps} from 'formik'
import {MessageType} from '../../../common/component/MessageToast'
import {IFilterDto} from '../../../common/model/Core.model'
import { TagInputSearchListControls } from '../../../common/component/TagInputSearchListControls'
import { useContactSearchMutation } from '../Contact.api'
import { ContactDashboardListItem } from './ContactDashboardListItem'
import { Link } from 'react-router-dom'
import classNames from 'classnames'

interface Props {
  data?: IContactDto[]
  formik?: FormikProps<any>
  fieldName?: string
  disabled?: boolean
  primaryLabel?: string
}

export const ContactEditor: FC<Props> = ({data, formik, fieldName, disabled, primaryLabel}: Props) => {
  const intl = useIntl()

  const [toastEntry, setToastEntry] = useState<[MessageType, string]>()

  const didFetchData = useRef(false)
  const filterKeys = {
    defaultKey: IContactFilter.DETAIL,
    defaultKeyList: [
      IContactFilter.ID,
      IContactFilter.DETAIL,
    ],
  }
  const [isLoading, setIsLoading] = useState(false)
  const [filterSuggestions, setFilterSuggestions] = useState<string[]>(filterKeys.defaultKeyList)
  const [filters, setFilters] = useState<IFilterDto[]>([])
  const [searchResult, setSearchResult] = useState<IContactDto[] | undefined>(undefined)
  const [contactSearch] = useContactSearchMutation()

  const searchOnClickHandler = async () => {
    try {
      if (!didFetchData.current) {
        setIsLoading(true)
        const response: any = await contactSearch({filters: filters})
        if (!response || response.error) {
          setToastEntry([MessageType.ERROR, intl.formatMessage({id: 'ERROR.MESSAGE.UNKNOWN'})])
        } else {
          setSearchResult(response.data.data)
          setFilters(response.data.filters)
        }
      }
    } catch (e: any) {
      setToastEntry([MessageType.ERROR, intl.formatMessage({id: 'ERROR.MESSAGE.UNKNOWN'})])
    } finally {
      setIsLoading(false)
    }
  }

  const resetSearchResetOnClickHandler = async () => {
    setSearchResult(undefined)
    setFilterSuggestions(filterKeys.defaultKeyList)
    setFilters([])
  }

  const rowOnClickHandler = async (id) => {
    if (searchResult) {
      const contact = searchResult.find((contact) => contact.id === id)
      if (contact && formik && fieldName) {
        const items = get(formik.values, fieldName)
        if (!items.find((item) => item.id === contact.id)) {
          const sortedItems: IContactDto[] = [...items, contact].sort((a, b) => {
            if (a.displayName < b.displayName) {
              return -1
            }
            if (a.displayName > b.displayName) {
              return 1
            }
            return 0
          })

          formik.setFieldValue(fieldName, sortedItems)
        }
      }
    }

    await resetSearchResetOnClickHandler()
  }

  const removeOnClick = (index) => {
    if (formik && fieldName) {
      formik.setFieldValue(fieldName, get(formik.values, fieldName).filter((_, i) => i !== index))
    }
  }

  const buildMainLine = (contact: IContactDto): string => {
    let description = ''
    if (contact.email && contact.telephone) {
      description += `${contact.email} | ${contact.telephone}`
    } else if (contact.email) {
      description += `${contact.email}`
    } else if (contact.telephone) {
      description += `${contact.telephone}`
    }

    if (!description && contact.details) {
      const emails = contact.details.filter((detail) => detail.type === ContactDetailsType.EMAIL)
      const telephones = contact.details.filter(
        (detail) => detail.type === ContactDetailsType.TELEPHONE
      )
      if (emails && emails.length > 0 && telephones && telephones.length > 0) {
        const email: string = emails[0].value ?? ''
        const telephone: string = telephones[0].value ?? ''
        description += `${email} | ${telephone}`
      } else if (emails && emails.length > 0) {
        description += `${emails[0].value ?? ''}`
      } else if (telephones && telephones.length > 0) {
        description += `${telephones[0].value ?? ''}`
      }
    }

    if (contact.address && contact.countryCode) {
      return (description += description
        ? ` | ${contact.address} | ${intl.formatMessage({
            id: `DROPDOWN.COUNTRY.${contact.countryCode}`,
          })}`
        : `${contact.address} | ${intl.formatMessage({
            id: `DROPDOWN.COUNTRY.${contact.countryCode}`,
          })}`)
    } else if (contact.address) {
      return (description += description ? ` | ${contact.address}` : `${contact.address}`)
    } else if (contact.countryCode) {
      return (description += description
        ? ` | ${intl.formatMessage({id: `DROPDOWN.COUNTRY.${contact.countryCode}`})}`
        : `${intl.formatMessage({id: `DROPDOWN.COUNTRY.${contact.countryCode}`})}`)
    }
    return description.toUpperCase()
  }

  return (
    <>
      {formik && fieldName && (
        <>
          <div className='px-5'>
            <div className='row'>
              <div className='row mb-2'>
                <div className='col-xxl-12'>
                  <TagInputSearchListControls
                    loading={isLoading}
                    enabled={!formik.isSubmitting}
                    filterKeys={filterKeys}
                    filterSuggestions={filterSuggestions}
                    filters={filters}
                    createOnClickPath={'/contacts/new'}
                    setFilterSuggestions={setFilterSuggestions}
                    setFilters={setFilters}
                    searchOnClickHandler={searchOnClickHandler}
                    resetSearchResetOnClickHandler={resetSearchResetOnClickHandler}
                  ></TagInputSearchListControls>
                </div>
              </div>
              <div className='row mb-2'>
                <div className='col-xxl-12 h-5px'>
                  {searchResult && searchResult.length > 0 && (
                    <ul id='contacts-autocomplete-list' className='suggestions-list' role='listbox'>
                      {searchResult.map((contact, index) => (
                        <li key={index} onClick={() => rowOnClickHandler(contact.id)}>
                          <ContactDashboardListItem data={contact} />
                          <div className='separator'></div>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className='px-8'>
            <div className='row'>
              {get(formik.values, fieldName) &&
                get(formik.values, fieldName).map((contact, index) => (
                  // <div key={index}>
                  //   <ContactEditListItem
                  //     index={index}
                  //     disabled={formik.isSubmitting}
                  //     dataReload={true}
                  //     data={get(formik.values, `${fieldName}.${index}`)}
                  //     formik={formik}
                  //     fieldName={`${fieldName}.${index}`}
                  //     removeOnClick={() => removeOnClick(index)}
                  //   ></ContactEditListItem>
                  // </div>

                  <div key={index} className='d-flex flex-stack py-2 bg-light-primary mb-1'>
                    <div className='d-flex align-items-center'>
                      <div className='row mx-0'>
                        <div className='d-flex justify-content-end align-items-center p-0'>
                          <button
                            type='button'
                            className='btn btn-sm btn-icon'
                            onClick={() => removeOnClick(index)}
                            disabled={formik.isSubmitting}
                          >
                            <i
                              className={classNames('bi bi-x fs-1 fw-bold', {
                                'text-danger': !formik.isSubmitting,
                                'text-secondary': formik.isSubmitting,
                              })}
                            ></i>
                          </button>
                        </div>
                      </div>

                      <div className='ms-0'>
                        <div className='d-flex flex-row justify-content-start align-items-center'>
                          {contact.id ? (
                            <Link to={`/contacts/${contact.id}`} target='_blank'>
                              <div className='lh-2 fs-7'>
                                <span>{contact.displayName}</span>
                              </div>
                            </Link>
                          ) : (
                            <div className='lh-2 fs-7'>
                              <span className='fw-bolder mb-2'>
                                {contact.displayName}
                              </span>
                            </div>
                          )}

                          <i
                            title={`${
                              !get(formik.values, `${fieldName}.${index}.primary`)
                                ? ''
                                : primaryLabel ?? intl.formatMessage({id: 'UTIL.PRIMARY_CONTACT'})
                            }`}
                            className={`mb-1 ms-3 fs-7 bi bi-star-fill cursor-pointer ${
                              !get(formik.values, `${fieldName}.${index}.primary`)
                                ? 'text-gray-300'
                                : 'text-primary'
                            }`}
                            onClick={() =>
                              !disabled &&
                              formik.setFieldValue(
                                `${fieldName}.${index}.primary`,
                                !get(formik.values, `${fieldName}.${index}.primary`)
                              )
                            }
                          />
                        </div>

                        <div className='text-gray-600 fs-8'>{buildMainLine(contact)}</div>
                      </div>
                    </div>
                  </div>
                ))}
            </div>
          </div>
        </>
      )}

      {data && (
        <div className='flex'>
          <div className='row'>
            <div className='card-scroll h-600px'>
              {data.map((contact, index) => (
                <div key={index} className='p-0 mb-1'>
                  <ContactPrimarySelectorListItem data={contact} dataReload={true} />
                  {data?.length - 1 !== index && <div className='separator my-2'></div>}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </>
  )
}
